import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useAuth } from '../lib/auth';
import { isDemoTab } from '../lib/session';
import logger from '../lib/logger';

const LanguageToggle = ({ variant = 'default', surface: surfaceProp = null }) => {
    const { i18n } = useTranslation();
    const { user, isAuthenticated, setPreferredLanguage } = useAuth();
    // Surface-scoped test-id suffix so Sidebar vs TopNav can be
    // targeted independently by automation. Falls back to 'default'
    // for the login-page mount. Callers can override via the
    // `surface` prop — used by Sidebar.jsx to give the mobile and
    // desktop copies distinct ids (sidebar / sidebar-mobile) so
    // strict-mode Playwright lookups resolve to a single element.
    const surface = surfaceProp || (variant === 'compact'
        ? 'sidebar'
        : variant === 'compact-inline'
            ? 'topnav'
            : 'default');

    const languages = [
        { code: 'en', name: 'English', flag: '🇬🇧' },
        { code: 'fr', name: 'Français', flag: '🇨🇦' },
        { code: 'es', name: 'Español', flag: '🇲🇽' }
    ];

    const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

    // P_MORNING_BRIEF — User Language Contract.
    // When the user is authenticated, persist their language on the
    // server so it survives logout, hard reload, new device. When
    // unauthenticated (login page), fall back to local-only switching.
    const changeLanguage = async (langCode) => {
        // Demo sessions are shared, throw-away personas: persisting a language
        // on them re-pins every visitor (and the server default wins on every
        // boot). Demo = local-only switch; real accounts keep server persistence.
        if (isAuthenticated && !isDemoTab() && user && typeof setPreferredLanguage === 'function') {
            try {
                await setPreferredLanguage(langCode);
                return;
            } catch (err) {
                // Server persistence failed (offline, 5xx) — still
                // flip the UI locally so the user isn't stuck.
                logger.warn('[LanguageToggle] persist failed; local-only fallback', err);
            }
        }
        i18n.changeLanguage(langCode);
        localStorage.setItem('i18nextLng', langCode);
    };

    // Compact variant for sidebar
    if (variant === 'compact') {
        return (
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full flex items-center justify-between gap-2 bg-muted hover:bg-accent border-border"
                        data-testid={`language-toggle-${surface}`}
                        data-testid-legacy="language-toggle"
                    >
                        <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4 text-muted-foreground" />
                            <span>{currentLang.flag} {currentLang.name}</span>
                        </div>
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                    {languages.map((lang) => (
                        <DropdownMenuItem
                            key={lang.code}
                            onClick={() => changeLanguage(lang.code)}
                            className={`cursor-pointer ${i18n.language === lang.code ? 'bg-blue-50 text-blue-700' : ''}`}
                            data-testid={`lang-${lang.code}-${surface}`}
                        >
                            <span className="mr-2 text-lg">{lang.flag}</span>
                            <span>{lang.name}</span>
                            {i18n.language === lang.code && (
                                <span className="ml-auto text-blue-600">✓</span>
                            )}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        );
    }

    // Default variant for login page / header
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex items-center gap-2 bg-background hover:bg-muted border-border shadow-sm"
                    data-testid={`language-toggle-${surface}`}
                    data-testid-legacy="language-toggle"
                >
                    <Globe className="w-4 h-4 text-slate-500" />
                    <span>{currentLang.flag} {currentLang.code.toUpperCase()}</span>
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
                {languages.map((lang) => (
                    <DropdownMenuItem
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`cursor-pointer ${i18n.language === lang.code ? 'bg-blue-50 text-blue-700' : ''}`}
                        data-testid={`lang-${lang.code}-${surface}`}
                    >
                        <span className="mr-2 text-lg">{lang.flag}</span>
                        <span>{lang.name}</span>
                        {i18n.language === lang.code && (
                            <span className="ml-auto text-blue-600">✓</span>
                        )}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default LanguageToggle;
